import React, { useState, useEffect } from "react";
import SuperLayout from "../layout/SuperAdminLayout";
import { useAuth } from "../../context/AuthContext";
import { FetchSettings, AdminUpdateSettings, getSiteSettings, updateSiteSettings } from "../../services/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import GatewaySettings from "./GatewaySettings";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";

const SuperSecuritySettings = () => {
  const { user } = useAuth();

  const [saveActivityLogs, setSaveActivityLogs] = useState(true);
  const [emailOnUnusualActivity, setEmailOnUnusualActivity] = useState(true);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [twoFactorOpen, setTwoFactorOpen] = useState(false);

  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [contactFormUrl, setContactFormUrl] = useState("");
  const [contactFormSaving, setContactFormSaving] = useState(false);
  const [contactFormMsg, setContactFormMsg] = useState("");

  const fullName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "Sunday Arjeniwa";
  const email = user?.email;

  const getInitials = (name) => {
    if (!name) return "SA";
    return name.trim().split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await FetchSettings();
        setSaveActivityLogs(settings.save_activity_logs ?? true);
        setEmailOnUnusualActivity(settings.email_on_unusual_activity ?? true);
        setTwoFactorEnabled(settings.two_factor_enabled ?? false);
      } catch (err) {
        setErrorMsg("Failed to load security settings");
      }
    };
    const loadSiteSettings = async () => {
      try {
        const s = await getSiteSettings();
        setContactFormUrl(s.contact_form_url || "");
      } catch (_) {}
    };
    loadSettings();
    loadSiteSettings();
  }, []);

  const handleSaveContactFormUrl = async () => {
    setContactFormSaving(true);
    setContactFormMsg("");
    try {
      await updateSiteSettings({ contact_form_url: contactFormUrl });
      setContactFormMsg("Saved successfully.");
    } catch (_) {
      setContactFormMsg("Failed to save. Please try again.");
    } finally {
      setContactFormSaving(false);
    }
  };

  const handleToggleActivityLogs = async (checked) => {
    setSaveActivityLogs(checked);
    setLoading(true);
    setErrorMsg("");
    try {
      await AdminUpdateSettings({ save_activity_logs: checked });
    } catch (err) {
      setErrorMsg("Failed to save preference");
      setSaveActivityLogs(!checked);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUnusualEmail = async (checked) => {
    setEmailOnUnusualActivity(checked);
    setLoading(true);
    setErrorMsg("");
    try {
      await AdminUpdateSettings({ email_on_unusual_activity: checked });
    } catch (err) {
      setErrorMsg("Failed to save preference");
      setEmailOnUnusualActivity(!checked);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle2FA = (checked) => {
    setTwoFactorEnabled(checked);
    if (checked) setTwoFactorOpen(true);
  };

  const handleChangeEmail = async () => {
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    if (!newEmail || !currentPassword) {
      setErrorMsg("Email and current password are required");
      setLoading(false);
      return;
    }

    try {
      await AdminUpdateSettings({
        new_email: newEmail,
        current_password: currentPassword,
      });
      setSuccessMsg("Email change request sent. Check your inbox.");
      setChangeEmailOpen(false);
      setNewEmail("");
      setCurrentPassword("");
    } catch (err) {
      setErrorMsg(err.message || "Failed to change email");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorMsg("All fields are required");
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      await AdminUpdateSettings({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setSuccessMsg("Password changed successfully");
      setChangePasswordOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setErrorMsg(err.message || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      await AdminUpdateSettings({ enable_2fa: true });
      setSuccessMsg("2FA enabled successfully");
      setTwoFactorOpen(false);
    } catch (err) {
      setErrorMsg(err.message || "Failed to enable 2FA");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SuperLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Security Settings</h1>
            <p className="text-gray-600 mt-1">
              Protect your account with these security options
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl shadow-sm p-8 flex flex-col items-center">
            <div className="w-48 h-48 rounded-full bg-blue-100 flex items-center justify-center text-6xl font-bold text-blue-600 border-4 border-blue-200">
              {getInitials(fullName)}
            </div>

            <span className="mt-6 inline-block bg-green-100 text-green-800 text-xs font-semibold px-4 py-1 rounded-full">
              {user?.role?.toUpperCase() || "ADMIN"}
            </span>

            <h2 className="mt-4 text-2xl font-bold text-gray-900">{fullName}</h2>
            <p className="text-gray-500 text-sm">{email}</p>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm">
              <div className="px-8 py-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Security Settings</h3>
                <p className="text-sm text-gray-600 mt-1">
                  These settings help keep your account secure.
                </p>
              </div>

              <div className="p-8 space-y-8">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-gray-900">Save my Activity Logs</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Save all activity logs including unusual activity detected.
                    </p>
                  </div>
                  <Switch
                    checked={saveActivityLogs}
                    onCheckedChange={handleToggleActivityLogs}
                    disabled={loading}
                  />
                </div>

                <hr className="border-gray-200" />

                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      Email me on unusual activity
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Receive email notifications for suspicious login attempts.
                    </p>
                  </div>
                  <Switch
                    checked={emailOnUnusualActivity}
                    onCheckedChange={handleToggleUnusualEmail}
                    disabled={loading}
                  />
                </div>

                <hr className="border-gray-200" />

                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium text-gray-900">Change Email Address</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Update your current email address.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setChangeEmailOpen(true)}
                  >
                    Change Email
                  </Button>
                </div>

                <hr className="border-gray-200" />

                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium text-gray-900">Change Password</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Set a new password to protect your account.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setChangePasswordOpen(true)}
                  >
                    Change Password
                  </Button>
                </div>

                <hr className="border-gray-200" />

                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      Two-Factor Authentication (2FA)
                      <span
                        className={`text-xs font-semibold px-3 py-1 rounded-full ${
                          twoFactorEnabled
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {twoFactorEnabled ? "Enabled" : "Disabled"}
                      </span>
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Add an extra layer of security with 2FA using your mobile device.
                    </p>
                  </div>
                  <Switch
                    checked={twoFactorEnabled}
                    onCheckedChange={handleToggle2FA}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={changeEmailOpen} onOpenChange={setChangeEmailOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Email Address</DialogTitle>
            <DialogDescription>
              Enter your new email and current password to update.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div>
              <Label htmlFor="newEmail">New Email</Label>
              <Input
                id="newEmail"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="currentPasswordEmail">Current Password</Label>
              <Input
                id="currentPasswordEmail"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="mt-1"
              />
            </div>

            {errorMsg && <p className="text-red-600 text-sm mt-2">{errorMsg}</p>}
            {successMsg && <p className="text-green-600 text-sm mt-2">{successMsg}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeEmailOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleChangeEmail} disabled={loading}>
              {loading ? "Updating..." : "Update Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div>
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1"
              />
            </div>

            {errorMsg && <p className="text-red-600 text-sm mt-2">{errorMsg}</p>}
            {successMsg && <p className="text-green-600 text-sm mt-2">{successMsg}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePasswordOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleChangePassword} disabled={loading}>
              {loading ? "Updating..." : "Change Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Site Configuration */}
      <div className="mt-8 bg-white rounded-xl shadow-sm">
        <div className="px-8 py-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Site Configuration</h3>
          <p className="text-sm text-gray-600 mt-1">Control public-facing settings for the patient portal.</p>
        </div>
        <div className="p-8 space-y-6">
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-1 block">Contact / Support Form URL</Label>
            <p className="text-xs text-gray-500 mb-3">
              Paste the embed URL from Google Forms, Tally.so, Typeform, or any other form provider. It will appear on the patient Support page as an iframe.
            </p>
            <div className="flex gap-3">
              <Input
                type="url"
                placeholder="https://docs.google.com/forms/d/e/.../viewform?embedded=true"
                value={contactFormUrl}
                onChange={(e) => setContactFormUrl(e.target.value)}
                className="flex-1 text-sm"
              />
              <Button
                onClick={handleSaveContactFormUrl}
                disabled={contactFormSaving}
                className="bg-[#0d3d2e] hover:bg-[#0f4a38] text-white text-sm px-5"
              >
                {contactFormSaving ? "Saving…" : "Save"}
              </Button>
            </div>
            {contactFormMsg && (
              <p className={`text-xs mt-2 ${contactFormMsg.includes("success") ? "text-green-600" : "text-red-500"}`}>
                {contactFormMsg}
              </p>
            )}
            {contactFormUrl && (
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Preview:</p>
                <iframe
                  src={contactFormUrl}
                  width="100%"
                  height="300"
                  frameBorder="0"
                  title="Form Preview"
                  className="rounded-xl border border-gray-100"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <GatewaySettings gateway="monnify" />
      <GatewaySettings gateway="remita" />

      <Dialog open={twoFactorOpen} onOpenChange={setTwoFactorOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Add an extra layer of security to your account.
            </DialogDescription>
          </DialogHeader>

          <div className="py-6 space-y-6">
            <p className="text-sm text-gray-600">
              After enabling 2FA, you will need to scan this QR code with your authenticator app.
            </p>

            <div className="flex justify-center">
              <div className="w-48 h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                QR Code Placeholder
              </div>
            </div>

            <p className="text-sm text-gray-500 text-center">
              Or enter this code manually: <strong>ABCD-EFGH-IJKL-MNOP</strong>
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTwoFactorOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEnable2FA} disabled={loading}>
              {loading ? "Enabling..." : "Enable 2FA"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SuperLayout>
  );
};

export default SuperSecuritySettings;